import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, mxn, fmtDate } from "@/lib/api";
import { Printer } from "lucide-react";

export default function ReporteCobranzaPrint() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const mode = params.get("mode") || "pendiente";

  useEffect(() => {
    const qs = params.toString();
    api.get(`/cobranza-report${qs ? `?${qs}` : ""}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [params]);

  useEffect(() => {
    document.body.style.background = "#dfe4ea";
    return () => (document.body.style.background = "");
  }, []);

  if (data === null) return <div className="p-10 font-mono-data text-[#5B6672]">Generando reporte…</div>;
  if (data === false) return <div className="p-10 font-mono-data text-[#ED1F26]">No se pudo generar el reporte.</div>;

  const { client, organization, facturas, remisiones, payments, totals, company } = data;
  const title = mode === "pendiente" ? "REPORTE DE SALDOS PENDIENTES" : "ESTADO DE CUENTA GENERAL";

  // Filtros previos del panel (Clientes → Estado de cuenta)
  const docType = params.get("doc_type") || "ambas";        // facturas | remisiones | ambas
  const payStatus = params.get("pay_status") || "ambas";     // pendientes | pagadas | ambas
  const matchPay = (d) =>
    payStatus === "ambas" ? true : payStatus === "pagadas" ? (d.balance || 0) <= 0.009 : (d.balance || 0) > 0.009;
  const showFacturas = docType === "facturas" || docType === "ambas";
  const showRemisiones = docType === "remisiones" || docType === "ambas";
  const facturasF = showFacturas ? facturas.filter(matchPay) : [];
  const remisionesF = showRemisiones ? remisiones.filter(matchPay) : [];
  const showPagos = mode === "general" && docType === "ambas" && payStatus === "ambas";
  const sum = (arr, k) => arr.reduce((a, x) => a + (x[k] || 0), 0);
  const tImporte = sum(facturasF, "total") + sum(remisionesF, "total");
  const tCobrado = sum(facturasF, "amount_paid") + sum(remisionesF, "amount_paid");
  const tSaldo = sum(facturasF, "balance") + sum(remisionesF, "balance");
  const docLabel = { facturas: "Facturas", remisiones: "Remisiones", ambas: "Facturas y remisiones" }[docType];
  const payLabel = { pendientes: "Pendientes de pago", pagadas: "Pagadas", ambas: "Pendientes y pagadas" }[payStatus];

  return (
    <div className="min-h-screen py-8 flex flex-col items-center">
      <div className="no-print mb-6 flex gap-3">
        <button onClick={() => window.print()} data-testid="print-cobranza-button"
          className="inline-flex items-center gap-2 bg-[#465B7F] hover:bg-[#374A66] text-white px-5 py-2.5 text-sm uppercase tracking-widest transition-colors">
          <Printer size={16} /> Imprimir reporte
        </button>
        <button onClick={() => window.close()} className="px-5 py-2.5 text-sm uppercase tracking-widest border border-[#16202B] text-[#16202B] hover:bg-[#16202B] hover:text-white transition-colors">
          Cerrar
        </button>
      </div>

      <div className="bg-white shadow-lg" style={{ width: "279mm", minHeight: "180mm", padding: "16mm" }}>
        <div className="h-1.5 brand-gradient -mx-[16mm] -mt-[16mm] mb-6" />
        <div className="flex justify-between items-start border-b-2 border-[#16202B] pb-4">
          <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="w-44" />
          <div className="text-right">
            <div className="font-display font-bold text-2xl text-[#465B7F]">{title}</div>
            <div className="font-mono-data text-xs text-[#5B6672] mt-1">{organization ? `Organización: ${organization.name}` : `Cliente: ${client?.name || "General (todos los clientes)"}`}</div>
            <div className="font-mono-data text-xs text-[#5B6672]">Emitido: {fmtDate(new Date().toISOString())}</div>
            <div className="font-mono-data text-xs text-[#465B7F] mt-1">Filtro: {docLabel} · {payLabel}</div>
          </div>
        </div>

        {/* Facturas */}
        {showFacturas && (
        <>
        <div className="mt-6 overline">Facturas</div>
        <table className="w-full mt-2 font-mono-data text-sm">
          <thead>
            <tr className="border-b-2 border-[#16202B] text-left">
              <th className="py-2 uppercase text-xs tracking-widest">Folio</th>
              <th className="py-2 uppercase text-xs tracking-widest">Fecha</th>
              {!client && <th className="py-2 uppercase text-xs tracking-widest">Cliente</th>}
              <th className="py-2 uppercase text-xs tracking-widest">Vence</th>
              <th className="py-2 uppercase text-xs tracking-widest text-right">Total</th>
              <th className="py-2 uppercase text-xs tracking-widest text-right">Cobrado</th>
              <th className="py-2 uppercase text-xs tracking-widest text-right">Saldo</th>
              <th className="py-2 uppercase text-xs tracking-widest">Estatus</th>
            </tr>
          </thead>
          <tbody>
            {facturasF.length === 0 ? (
              <tr><td colSpan={8} className="py-4 text-center text-[#5B6672]">Sin facturas para el filtro seleccionado.</td></tr>
            ) : facturasF.map((f) => (
              <tr key={f.id} className="border-b border-[#E3E8EE]">
                <td className="py-2 text-[#16202B]">{f.number}</td>
                <td className="py-2">{fmtDate(f.created_at)}</td>
                {!client && <td className="py-2">{f.client?.name || "—"}</td>}
                <td className="py-2">{fmtDate(f.due_date)}</td>
                <td className="py-2 text-right">{mxn(f.total)}</td>
                <td className="py-2 text-right text-[#49AC9A]">{mxn(f.amount_paid)}</td>
                <td className="py-2 text-right text-[#ED1F26]">{mxn(f.balance)}</td>
                <td className="py-2">{f.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
        )}

        {/* Pagos (general) */}
        {showPagos && (
          <>
            <div className="mt-8 overline">Pagos recibidos</div>
            <table className="w-full mt-2 font-mono-data text-sm">
              <thead>
                <tr className="border-b-2 border-[#16202B] text-left">
                  <th className="py-2 uppercase text-xs tracking-widest">Fecha de pago</th>
                  <th className="py-2 uppercase text-xs tracking-widest">Factura</th>
                  {!client && <th className="py-2 uppercase text-xs tracking-widest">Cliente</th>}
                  <th className="py-2 uppercase text-xs tracking-widest">Método</th>
                  <th className="py-2 uppercase text-xs tracking-widest">Banco</th>
                  <th className="py-2 uppercase text-xs tracking-widest">Referencia</th>
                  <th className="py-2 uppercase text-xs tracking-widest text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={7} className="py-4 text-center text-[#5B6672]">Sin pagos registrados.</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id} className="border-b border-[#E3E8EE]">
                    <td className="py-2">{fmtDate(p.date)}</td>
                    <td className="py-2 text-[#16202B]">{p.invoice_number}</td>
                    {!client && <td className="py-2">{p.client?.name || "—"}</td>}
                    <td className="py-2">{p.method}</td>
                    <td className="py-2">{p.bank || "—"}</td>
                    <td className="py-2">{p.reference || "—"}</td>
                    <td className="py-2 text-right text-[#49AC9A]">{mxn(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Remisiones */}
        {showRemisiones && remisionesF.length > 0 && (
          <>
            <div className="mt-8 overline">Remisiones no facturadas</div>
            <table className="w-full mt-2 font-mono-data text-sm">
              <thead>
                <tr className="border-b-2 border-[#16202B] text-left">
                  <th className="py-2 uppercase text-xs tracking-widest">Folio</th>
                  <th className="py-2 uppercase text-xs tracking-widest">Fecha</th>
                  {!client && <th className="py-2 uppercase text-xs tracking-widest">Cliente</th>}
                  <th className="py-2 uppercase text-xs tracking-widest">Vence</th>
                  <th className="py-2 uppercase text-xs tracking-widest text-right">Total</th>
                  <th className="py-2 uppercase text-xs tracking-widest text-right">Cobrado</th>
                  <th className="py-2 uppercase text-xs tracking-widest text-right">Saldo</th>
                  <th className="py-2 uppercase text-xs tracking-widest">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {remisionesF.map((r) => (
                  <tr key={r.id} className="border-b border-[#E3E8EE]">
                    <td className="py-2 text-[#16202B]">{r.number}</td>
                    <td className="py-2">{fmtDate(r.created_at)}</td>
                    {!client && <td className="py-2">{r.client?.name || "—"}</td>}
                    <td className="py-2">{fmtDate(r.due_date)}</td>
                    <td className="py-2 text-right">{mxn(r.total)}</td>
                    <td className="py-2 text-right text-[#49AC9A]">{mxn(r.amount_paid)}</td>
                    <td className="py-2 text-right text-[#ED1F26]">{mxn(r.balance)}</td>
                    <td className="py-2">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Totales del filtro aplicado */}
        <div className="flex justify-end mt-8">
          <div className="w-80 font-mono-data text-sm space-y-1">
            <div className="flex justify-between"><span className="text-[#5B6672]">Total (importe)</span><span>{mxn(tImporte)}</span></div>
            <div className="flex justify-between"><span className="text-[#5B6672]">Cobrado</span><span className="text-[#49AC9A]">{mxn(tCobrado)}</span></div>
            <div className="flex justify-between border-t-2 border-[#16202B] pt-1 text-base font-semibold">
              <span>Saldo total</span><span className="text-[#ED1F26]">{mxn(tSaldo)}</span>
            </div>
            <div className="font-mono-data text-[10px] text-[#5B6672] pt-1">
              Incluye {facturasF.length} factura(s) y {remisionesF.length} remisión(es) según el filtro: {docLabel} · {payLabel}.
            </div>
          </div>
        </div>

        <div className="mt-10 font-mono-data text-xs text-center text-[#5B6672] border-t border-[#E3E8EE] pt-4">
          {company?.name || "TAS Publicidad 360"}
        </div>
      </div>
    </div>
  );
}
